import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Loader, FileEdit } from 'lucide-react';
import './FindingForm.css';

const STANDARDS = [
  'ISO 27001',
  'ISO 9001',
  'ISO 45001',
  'ISCC',
  'Other',
];

const SEVERITY_OPTIONS = [
  { value: 'CRITICAL', label: 'Critical', icon: '🔴', color: '#dc3545' },
  { value: 'MAJOR', label: 'Major', icon: '🟠', color: '#fd7e14' },
  { value: 'MINOR', label: 'Minor', icon: '🟡', color: '#ffc107' },
];

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

/**
 * FindingForm Component
 * Form for creating and editing audit findings
 */
const FindingForm = ({
  finding = null,
  onSubmit = () => {},
  onCancel = () => {},
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    standard: '',
    requirement: '',
    severity: 'MAJOR',
    description: '',
    evidence: '',
    rootCause: '',
    status: 'OPEN',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Initialize form with existing finding data
  useEffect(() => {
    if (finding) {
      setFormData({
        standard: finding.standard || '',
        requirement: finding.requirement || '',
        severity: finding.severity || 'MAJOR',
        description: finding.description || '',
        evidence: finding.evidence || '',
        rootCause: finding.rootCause || '',
        status: finding.status || 'OPEN',
      });
    }
  }, [finding]);

  // Validation rules
  const validateField = useCallback((name, value) => {
    const fieldErrors = { ...errors };
    delete fieldErrors[name];

    switch (name) {
      case 'standard':
        if (!value.trim()) {
          fieldErrors.standard = 'Standard is required';
        }
        break;
      case 'requirement':
        if (!value.trim()) {
          fieldErrors.requirement = 'Requirement is required';
        } else if (value.length > 500) {
          fieldErrors.requirement = 'Requirement must be 500 characters or less';
        }
        break;
      case 'description':
        if (!value.trim()) {
          fieldErrors.description = 'Description is required';
        }
        break;
      case 'severity':
        if (!value) {
          fieldErrors.severity = 'Severity is required';
        }
        break;
      default:
        break;
    }

    setErrors(fieldErrors);
    return Object.keys(fieldErrors).length === 0;
  }, [errors]);

  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      validateField(name, value);
    }
  }, [touched, validateField]);

  // Handle field blur
  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  }, [validateField]);

  // Handle form submit
  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};

    if (!formData.standard.trim()) {
      newErrors.standard = 'Standard is required';
    }
    if (!formData.requirement.trim()) {
      newErrors.requirement = 'Requirement is required';
    } else if (formData.requirement.length > 500) {
      newErrors.requirement = 'Requirement must be 500 characters or less';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.severity) {
      newErrors.severity = 'Severity is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({
        standard: true,
        requirement: true,
        severity: true,
        description: true,
      });
      return;
    }

    onSubmit(formData);
  }, [formData, onSubmit]);

  const charCount = formData.requirement.length;

  return (
    <div className="finding-form-card card">
      <div className="card-header finding-form-header">
        <div className="d-flex align-items-center gap-2">
          <FileEdit size={18} className="text-primary" />
          <h6 className="mb-0 fw-semibold">
            {finding ? 'Edit Finding' : 'Create New Finding'}
          </h6>
        </div>
        <p className="text-muted small mb-0 mt-1">
          {finding
            ? 'Update the details of this audit finding below.'
            : 'Complete the form below to record a new audit finding.'}
        </p>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit} className="finding-form">
          {/* Row 1: Standard + Status */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <label htmlFor="standard" className="form-label">
                Standard <span className="text-danger">*</span>
              </label>
              <select
                id="standard"
                name="standard"
                className={`form-select ${touched.standard && errors.standard ? 'is-invalid' : ''}`}
                value={formData.standard}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}
              >
                <option value="">Select a standard...</option>
                {STANDARDS.map((std) => (
                  <option key={std} value={std}>
                    {std}
                  </option>
                ))}
              </select>
              {touched.standard && errors.standard && (
                <div className="invalid-feedback d-block">{errors.standard}</div>
              )}
            </div>

            <div className="col-12 col-md-6">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleChange}
                disabled={isLoading}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Requirement */}
          <div className="mb-3">
            <label htmlFor="requirement" className="form-label">
              Requirement <span className="text-danger">*</span>
              <span className="float-end text-muted char-counter">
                {charCount}/500
              </span>
            </label>
            <textarea
              id="requirement"
              name="requirement"
              className={`form-control ${touched.requirement && errors.requirement ? 'is-invalid' : ''}`}
              rows="3"
              maxLength="500"
              placeholder="Describe the audit requirement or finding..."
              value={formData.requirement}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isLoading}
            />
            {touched.requirement && errors.requirement && (
              <div className="invalid-feedback d-block">{errors.requirement}</div>
            )}
          </div>

          {/* Severity */}
          <div className="mb-3">
            <label className="form-label d-block">
              Severity <span className="text-danger">*</span>
            </label>
            <div className="severity-options">
              {SEVERITY_OPTIONS.map((option) => (
                <div key={option.value} className="form-check severity-radio">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={`severity-${option.value}`}
                    name="severity"
                    value={option.value}
                    checked={formData.severity === option.value}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                  <label
                    className="form-check-label severity-label"
                    htmlFor={`severity-${option.value}`}
                    style={{ borderLeftColor: option.color }}
                  >
                    <span className="severity-badge" style={{ backgroundColor: option.color }}>
                      {option.icon}
                    </span>
                    <span className="severity-text">{option.label}</span>
                  </label>
                </div>
              ))}
            </div>
            {touched.severity && errors.severity && (
              <div className="text-danger small mt-2">{errors.severity}</div>
            )}
          </div>

          {/* Description */}
          <div className="mb-3">
            <label htmlFor="description" className="form-label">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              className={`form-control ${touched.description && errors.description ? 'is-invalid' : ''}`}
              rows="4"
              placeholder="Provide detailed description of the finding..."
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isLoading}
            />
            {touched.description && errors.description && (
              <div className="invalid-feedback d-block">{errors.description}</div>
            )}
          </div>

          {/* Row: Evidence + Root Cause */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6">
              <label htmlFor="evidence" className="form-label">
                Evidence <span className="text-muted fw-normal">(Optional)</span>
              </label>
              <textarea
                id="evidence"
                name="evidence"
                className="form-control"
                rows="3"
                placeholder="Document evidence supporting this finding..."
                value={formData.evidence}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </div>

            <div className="col-12 col-md-6">
              <label htmlFor="rootCause" className="form-label">
                Root Cause <span className="text-muted fw-normal">(Optional)</span>
              </label>
              <textarea
                id="rootCause"
                name="rootCause"
                className="form-control"
                rows="3"
                placeholder="Analyze root causes of this finding..."
                value={formData.rootCause}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="d-flex flex-wrap gap-2 finding-form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader className="spinner-icon me-1" size={15} />
                  Saving...
                </>
              ) : (
                finding ? 'Update Finding' : 'Create Finding'
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FindingForm;
